import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import useMarkedMessage from '../components/SignerUi/hooks/useMarkedMessage';
import useNotifications from './useNotifications';
import {selectLocale} from '../components/appSlice';
import {DEFAULT_LOCALE} from '../../constants';
import {BASE_PATH} from '../../constants/routes';
import {getErrorText} from '@utils/requests/error';

let defaultLocaleMessages = null;
let defaultLocaleRequest = null;

/**
 * Loads the source-locale messages once and keeps them for the rest of the session.
 * The reverse lookup below needs the whole map, which is no longer bundled with the client -
 * the server holds the edition downloaded from the localization service and serves it per locale.
 */
function setRequestedDefaultLocaleMessages() {
    if (defaultLocaleRequest || typeof window === 'undefined') {
        return;
    }

    defaultLocaleRequest = fetch(`${BASE_PATH}/i18n/${DEFAULT_LOCALE}`)
        .then((response) => {
            if (!response.ok) {
                throw new Error(`Responded ${response.status}`);
            }

            return response.json();
        })
        .then((messages) => {
            defaultLocaleMessages = messages;
        })
        .catch((error) => {
            // eslint-disable-next-line no-console
            console.error(`Could not load ${DEFAULT_LOCALE} messages for error-key lookup`, error);
            defaultLocaleRequest = null;
        });
}

export default function useErrorNotification(error, options = {}, notificationOptions) {
    const intl = useIntl();
    const toastId = useRef(null);
    const {showErrorAlert} = useNotifications();
    const parameters = !isEmpty(options) ? options : get(error, 'parameters', {});
    const messageKey = get(error, 'messageKey', '');

    useEffect(() => {
        if (!messageKey && toastId.current) {
            toast.dismiss(toastId.current);
        }
        if (messageKey) {
            toastId.current = showErrorAlert(getErrorText({...error, parameters}, intl), notificationOptions);
        }
    }, [error]);

    return null;
}

function isMessageKey(str) {
    // A regular expression to test if the string is like a key for example shared.about
    // This pattern checks for strings that contain one or more periods without spaces
    const keyPattern = /^[^. ]+(\.[^. ]+)+$/;

    return keyPattern.test(str);
}

// This workaround needs to be because some error messages doesn't have a key.key. RFT-158
function findMessageKeyByText(text) {
    setRequestedDefaultLocaleMessages();

    if (!defaultLocaleMessages) {
        return text;
    }

    for (const key in defaultLocaleMessages) {
        if (defaultLocaleMessages[key] === text) {
            return key;
        }
    }
    return text;
}

function getMessageKey(error, locale) {
    const messageKey = get(error, 'messageKey', '');
    if (isMessageKey(messageKey)) {
        return messageKey;
    }
    return findMessageKeyByText(messageKey, locale);
}

export function useGraphQlErrorNotification(error, reset, params) {
    const formatMessage = useMarkedMessage();
    const {showErrorAlert} = useNotifications();
    const locale = useSelector(selectLocale);
    const messageKey = error ? getMessageKey(error, locale) : '';
    const parameters = get(error, 'parameters', {});

    useEffect(() => {
        if (error) {
            showErrorAlert(formatMessage({
                key: messageKey || 'oss.generic.defaultError',
                options: params || parameters
            }));
            if (reset) {
                reset();
            }
        }
    }, [error]);

    return null;
}
